/**
 * Leitura da "ficha do veículo" que o SGR (Hinova) escreve na descrição do
 * ticket, e do título `PLACA-CIDADE-NOME` (spec 016).
 *
 * O formato é do worker da PoolTrack (pooltrack/src/sync-sgr-tiflux/pubsub.ts):
 * `<p>ID_SGR: X<br>CHASSI: X<br>…<br>TELEFONE: X[<br>TELEFONE ADICIONAL: X]</p>`.
 * Três coisas mudam o texto no caminho e por isso a leitura é tolerante:
 *
 * - o TiFlux re-serializa o HTML (`<br>` → `<br />`, espaço → `&nbsp;`);
 * - o worker interpola sem fallback: sem telefone ativo sai `TELEFONE: undefined`
 *   por extenso — lido como vazio, nunca como telefone;
 * - o seed (e tickets antigos) usam outra variante: `MARCA/MODELO`, `COR`,
 *   `ANO`, `ENDERECO`, `NOME`.
 *
 * Extração DETERMINÍSTICA (FR-003): chave conhecida → campo; o resto é
 * ignorado. Valor vazio vira `undefined` — a OS abre com o campo em branco em
 * vez de um palpite. Puro, sem dependência: roda na API (rascunho) e no front.
 */
// só o TIPO: profiles.ts importa normalizeForCompare daqui, e um import de
// valor no sentido contrário fecharia o ciclo
import type { Terms } from './profiles';

export interface SgrVehicleSheet {
  /** true quando ao menos uma chave conhecida foi encontrada */
  recognized: boolean;
  sgrId?: string;
  chassis?: string;
  plate?: string;
  brand?: string;
  model?: string;
  color?: string;
  year?: string;
  /** nome do associado, quando a ficha traz (variante do seed) */
  name?: string;
  registeredAt?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zip?: string;
  /** TELEFONE e TELEFONE ADICIONAL, na ordem em que aparecem, sem repetição */
  phones: string[];
  /**
   * O valor da PRIMEIRA linha `TELEFONE:` com valor (spec 031): a camada da
   * descrição do slot `phone` da ficha. `phones` continua sendo a lista de
   * sempre — é ela que vale quando ninguém editou os telefones da ficha.
   */
  phone?: string;
  /** idem, da primeira linha `TELEFONE ADICIONAL:` (o slot `phoneAlt`) */
  phoneAlt?: string;
}

/**
 * Os campos da ficha como ela é MOSTRADA (sgrSheetEntries) e entregue ao
 * portal e ao bot (publicTicketSheet, spec 031): sem o `recognized` e sem os
 * telefones por chave — a lista `phones` basta.
 */
export type SgrSheetFields = Omit<SgrVehicleSheet, 'recognized' | 'phone' | 'phoneAlt'>;

export interface SgrTitle {
  plate?: string;
  city?: string;
  name?: string;
}

type SheetField = Exclude<keyof SgrVehicleSheet, 'recognized' | 'phones'>;

/** chave normalizada (sem acento, maiúscula, espaço único) → campo */
type SheetFieldKey = Exclude<SheetField, 'phone' | 'phoneAlt'>;

const KEY_TO_FIELD: Record<string, SheetFieldKey | 'phone' | 'phoneAlt' | 'brandModel'> = {
  ID_SGR: 'sgrId',
  'ID SGR': 'sgrId',
  CHASSI: 'chassis',
  PLACA: 'plate',
  MARCA: 'brand',
  MODELO: 'model',
  'MARCA/MODELO': 'brandModel',
  COR: 'color',
  ANO: 'year',
  NOME: 'name',
  'DATA/HORA CADASTRO': 'registeredAt',
  LOGRADOURO: 'street',
  ENDERECO: 'street',
  NUMERO: 'number',
  COMPLEMENTO: 'complement',
  BAIRRO: 'neighborhood',
  CIDADE: 'city',
  ESTADO: 'state',
  UF: 'state',
  CEP: 'zip',
  TELEFONE: 'phone',
  'TELEFONE ADICIONAL': 'phoneAlt',
};

/** Os campos que a dica do editor da descrição cita, na ordem em que aparecem. */
const HINT_FIELDS: ReadonlyArray<SheetFieldKey | 'phone' | 'phoneAlt' | 'brandModel'> = [
  'plate',
  'name',
  'phone',
  'phoneAlt',
  'brandModel',
  'color',
  'year',
  'city',
];

/**
 * As chaves mais usadas na grafia do worker (maiúsculas) — não todas. Até a
 * spec 031 a tela as ensinava ao editar a descrição (pedido da PoolTrack,
 * 03/10); a ficha agora tem campos próprios, e a lista fica como o contrato de
 * que quem ainda as escreve é lido (a camada da descrição). Saem do MESMO mapa da leitura: a dica não pode ensinar uma chave que o
 * parser não aceita. `Telefones:` e `Associado:` (os rótulos da ficha na tela)
 * NÃO são chaves: a busca por telefone também lê só `TELEFONE` e `TELEFONE
 * ADICIONAL` (erp.ticket_phones), e uma terceira grafia faria a ficha e a busca
 * discordarem.
 */
export function sgrSheetHintKeys(): Array<{ key: string; field: SheetFieldKey | 'phone' | 'brandModel' }> {
  const out: Array<{ key: string; field: SheetFieldKey | 'phone' | 'brandModel' }> = [];
  for (const field of HINT_FIELDS) {
    for (const [key, mapped] of Object.entries(KEY_TO_FIELD)) {
      // a dica fala dos dois telefones como "telefone" (a lista do disparo)
      if (mapped === field) out.push({ key, field: field === 'phoneAlt' ? 'phone' : field });
    }
  }
  return out;
}

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  // o editor do TiFlux escapa acentuadas por nome
  Aacute: 'Á', aacute: 'á', Acirc: 'Â', acirc: 'â', Atilde: 'Ã', atilde: 'ã', Agrave: 'À', agrave: 'à',
  Eacute: 'É', eacute: 'é', Ecirc: 'Ê', ecirc: 'ê',
  Iacute: 'Í', iacute: 'í',
  Oacute: 'Ó', oacute: 'ó', Ocirc: 'Ô', ocirc: 'ô', Otilde: 'Õ', otilde: 'õ',
  Uacute: 'Ú', uacute: 'ú', Uuml: 'Ü', uuml: 'ü',
  Ccedil: 'Ç', ccedil: 'ç',
  ordm: 'º', ordf: 'ª',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, ref: string) => {
    if (ref[0] === '#') {
      const code = ref[1] === 'x' || ref[1] === 'X' ? parseInt(ref.slice(2), 16) : parseInt(ref.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    return NAMED_ENTITIES[ref] ?? whole;
  });
}

/**
 * HTML de descrição → texto: `<br>`/fim de bloco viram quebra de linha, tags
 * somem, entidades são decodificadas, `&nbsp;` vira espaço comum. Usada também
 * para mandar a descrição à IA sem marcação.
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  const text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '');
  return decodeEntities(text)
    .replace(/ /g, ' ')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line) => line !== '')
    .join('\n');
}

/** Sem acento, sem caixa, espaço único — para comparar chave e cidade. */
export function normalizeForCompare(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/** "", "undefined" e "null" são o jeito do worker de dizer "não tem". */
function cleanValue(raw: string): string | undefined {
  const value = raw.trim();
  if (value === '' || value === 'undefined' || value === 'null') return undefined;
  return value;
}

export function parseSgrDescription(html: string | null | undefined): SgrVehicleSheet {
  const sheet: SgrVehicleSheet = {
    recognized: false,
    sgrId: undefined,
    chassis: undefined,
    plate: undefined,
    brand: undefined,
    model: undefined,
    color: undefined,
    year: undefined,
    name: undefined,
    registeredAt: undefined,
    street: undefined,
    number: undefined,
    complement: undefined,
    neighborhood: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
    phones: [],
    phone: undefined,
    phoneAlt: undefined,
  };

  for (const line of htmlToPlainText(html).split('\n')) {
    // chave até o PRIMEIRO ':' — "Data/Hora cadastro: 2026-09-20 14:32:00"
    // tem ':' no valor
    const match = /^([^:]{1,40}):(.*)$/.exec(line);
    if (!match) continue;
    const field = KEY_TO_FIELD[normalizeForCompare(match[1]!)];
    if (!field) continue;
    sheet.recognized = true;
    const value = cleanValue(match[2]!);
    if (value === undefined) continue;

    if (field === 'phone' || field === 'phoneAlt') {
      if (!sheet.phones.includes(value)) sheet.phones.push(value);
      // por chave, a primeira com valor (a camada da descrição dos slots)
      sheet[field] ??= value;
    } else if (field === 'brandModel') {
      // "VW/GOL 1.0 FLEX": a marca é o que vem antes da primeira barra
      const slash = value.indexOf('/');
      if (slash > 0) {
        sheet.brand ??= cleanValue(value.slice(0, slash));
        sheet.model ??= cleanValue(value.slice(slash + 1));
      } else {
        sheet.model ??= value;
      }
    } else {
      // primeira ocorrência vence (LOGRADOURO e ENDERECO nunca se sobrescrevem)
      sheet[field] ??= value;
    }
  }
  return sheet;
}

/** Placa no começo do título: Mercosul (ABC1D23) ou antiga (ABC1234 / ABC-1234). */
const LEADING_PLATE = /^([A-Za-z]{3}-?\d[A-Za-z0-9]\d{2})\s*-\s*(.+)$/;

/**
 * `PLACA-CIDADE-NOME` (ou com espaços em volta do hífen). O nome é tudo depois
 * da cidade — nome com hífen fica inteiro. Menos de três partes = não é título
 * de ficha, e nada é devolvido.
 */
export function parseSgrTitle(title: string | null | undefined): SgrTitle {
  const none: SgrTitle = { plate: undefined, city: undefined, name: undefined };
  if (!title) return none;
  const text = title.trim();

  let plate: string | undefined;
  let rest: string;
  const leading = LEADING_PLATE.exec(text);
  if (leading) {
    plate = leading[1]!;
    rest = leading[2]!;
  } else {
    const first = text.split(/\s*-\s*/);
    // sem placa reconhecível, só aceita um primeiro trecho com CARA de placa
    // (sem espaço, 5–8 alfanuméricos): "Trocar bomba - da piscina - hoje" não
    // pode virar placa "Trocar bomba"
    if (first.length < 3 || !/^[A-Za-z0-9]{5,8}$/.test(first[0]!)) return none;
    plate = first[0];
    rest = first.slice(1).join('-');
  }

  // " - " separa as partes quando existe; senão o hífen simples
  const spaced = rest.includes(' - ');
  const parts = spaced ? rest.split(/\s+-\s+/) : rest.split('-');
  if (parts.length < 2) return none;
  const city = cleanValue(parts[0]!);
  const name = cleanValue(parts.slice(1).join(spaced ? ' - ' : '-'));
  if (!plate || !city || !name) return none;
  return { plate: plate.trim(), city, name };
}

/**
 * A ficha em pares rótulo → valor, na ordem de leitura, para exibição (portal e
 * tela do ticket). Recurso, identificador e contato ("veículo", "placa",
 * "associado") vêm do vocabulário do tenant — spec 017: palavra de vertical só
 * existe nos presets, e a guarda (apps/api/src/shared/vertical-vocabulary.test)
 * reprovou estes rótulos quando moravam aqui escritos à mão. Os da origem não
 * nomeiam o sistema. Campo ausente não vira linha vazia.
 */
export function sgrSheetEntries(
  sheet: SgrSheetFields,
  terms: Pick<Terms, 'resource' | 'resourceId' | 'contact'>
): Array<{ label: string; value: string }> {
  // termo vem na forma do meio da frase ("placa"); rótulo começa maiúsculo
  const label = (term: string) => term.charAt(0).toUpperCase() + term.slice(1);
  const address = [
    [sheet.street, sheet.number].filter(Boolean).join(', '),
    sheet.complement,
    sheet.neighborhood,
  ]
    .filter((part) => part && part.trim() !== '')
    .join(' · ');
  const place = [sheet.city, sheet.state].filter(Boolean).join(' / ');
  const vehicle = [sheet.brand, sheet.model].filter(Boolean).join(' ');
  const rows: Array<[string, string | undefined]> = [
    [label(terms.resourceId), sheet.plate],
    [label(terms.resource), vehicle || undefined],
    ['Cor', sheet.color],
    ['Ano', sheet.year],
    ['Chassi', sheet.chassis],
    [label(terms.contact), sheet.name],
    ['Endereço', address || undefined],
    ['Cidade', place || undefined],
    ['CEP', sheet.zip],
    ['Telefones', sheet.phones.length ? sheet.phones.join(' · ') : undefined],
    ['Cadastrado em', sheet.registeredAt],
    ['Código do cadastro', sheet.sgrId],
  ];
  return rows
    .filter((row): row is [string, string] => typeof row[1] === 'string' && row[1].trim() !== '')
    .map(([label, value]) => ({ label, value }));
}
